import { MediaStoryCard } from '@components/display/cards';
import { CenteredEmptyState } from '@components/display/emptyStates';
import { YouTubeVideoDialog } from '@components/display/videos';
import { StickyTabs, Tabs } from '@components/utility/tabs';
import { isInfiniteLoadingItem } from '@hooks/useInfiniteLazyLoader';
import { Grid2 } from '@mui/material';
import { useOverviewVideos } from '@videos/hooks/useOverviewVideos';
import { TabValues } from '@videos/types';
import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';

export const VideosList = () => {
    const intl = useIntl();
    const [openVideoFullUrl, setOpenVideoFullUrl] = useState<string | null>(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const videoId = searchParams.get('v');
    const selectedTab = useMemo(() => {
        const currentTab = searchParams.get('tab') ?? TabValues.LATEST;
        return currentTab === TabValues.LATEST || currentTab === TabValues.MOST_VIEWED ? currentTab : TabValues.LATEST;
    }, [searchParams]);

    const { latest, mostViewed, isGetVideosError } = useOverviewVideos();
    const { videos, sentinelRef, hasRealItems, isLoadingMore } = selectedTab === TabValues.LATEST ? latest : mostViewed;

    return (
        <>
            <StickyTabs
                value={selectedTab}
                showGradient={true}
                onChange={(_event, newValue) => {
                    setSearchParams({ tab: newValue }, { preventScrollReset: true, replace: false });
                }}
                content={
                    <Grid2 container pb="24px" spacing="32px" position="relative" px={{ xs: 2, md: 4 }} py="24px">
                        {hasRealItems || isLoadingMore ? (
                            <>
                                {videos.map((video, index) =>
                                    isInfiniteLoadingItem(video) ? (
                                        <Grid2 key={`skeleton-${index}`} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                                            <MediaStoryCard
                                                key={`video-skeleton-${index}`}
                                                image={{
                                                    url: '/static/img/backgrounds/LeftSplitImageDesktop1.png',
                                                    alt_text: intl.formatMessage({
                                                        id: 'video.loadingThumbnail',
                                                        defaultMessage: 'Loading video thumbnail',
                                                    }),
                                                }}
                                                baseUrl=""
                                                date="Loading"
                                                slug="123"
                                                title="Loading"
                                                loading
                                            />
                                        </Grid2>
                                    ) : (
                                        <Grid2 key={`video-${video.slug}`} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                                            <MediaStoryCard
                                                key={`video-${video.slug}`}
                                                slug={video.slug}
                                                image={{
                                                    url: video.thumbnail ?? '',
                                                    alt_text: intl.formatMessage(
                                                        {
                                                            id: 'video.thumbnail',
                                                            defaultMessage: 'Thumbnail for {title}',
                                                        },
                                                        { title: video.title }
                                                    ),
                                                }}
                                                date={video.youtube_published_at}
                                                title={video.title ?? ''}
                                                link={{
                                                    fn: () => setOpenVideoFullUrl(video.youtube_url),
                                                    text: intl.formatMessage({
                                                        id: 'video.view',
                                                        defaultMessage: 'View Video',
                                                    }),
                                                }}
                                            />
                                        </Grid2>
                                    )
                                )}
                            </>
                        ) : isGetVideosError ? (
                            <CenteredEmptyState
                                message={
                                    <FormattedMessage
                                        id="videos.latest.error"
                                        defaultMessage="An error occurred while fetching the latest videos."
                                    />
                                }
                            />
                        ) : (
                            <CenteredEmptyState
                                message={
                                    <FormattedMessage
                                        id="videos.latest.empty"
                                        defaultMessage="No videos are available at the moment."
                                    />
                                }
                            />
                        )}
                        <div ref={sentinelRef} className="basis-full h-px" />
                    </Grid2>
                }
            >
                <Tabs.Tab
                    value={TabValues.LATEST}
                    label={<FormattedMessage id="videos.latest" defaultMessage="Latest Videos" />}
                />
                <Tabs.Tab
                    value={TabValues.MOST_VIEWED}
                    label={<FormattedMessage id="videos.mostViewed" defaultMessage="Most Viewed Videos" />}
                />
            </StickyTabs>
            <YouTubeVideoDialog
                videoId={videoId}
                fullUrl={openVideoFullUrl}
                onClose={() => {
                    setOpenVideoFullUrl(null);
                    setSearchParams((prev) => {
                        const newParams = new URLSearchParams(prev);
                        newParams.delete('v');
                        return newParams;
                    });
                }}
            />
        </>
    );
};
